import { NgStyle, NgTemplateOutlet } from '@angular/common';
import type { InputSignal, OnChanges, Signal, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  Input,
  Renderer2,
  ViewChild,
  output,
} from '@angular/core';

import type { Subscription } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { tokenToCssVar } from '@core/utils-legacy';

import type { AvatarToken } from './avatar-colors-set';
import { avatarTokenSet } from './avatar-colors-set';
import type {
  AvatarContext,
  AvatarFontSize,
  AvatarShape,
  AvatarSize,
  AvatarType,
} from './avatar.types';
import { AvatarRsvpBadge } from './avatar.types';
import type { IconName } from '../../icons-container/icons.type';
import type { IconToken } from '../icon/directives/icon-color.directive';
import type { IconSize } from '../icon/icon.component';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'rt-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'size + " " + shape + " " + type',
  },
  imports: [IconComponent, NgStyle, NgTemplateOutlet],
})
export class AvatarComponent implements OnChanges {
  public readonly AvatarRsvpBadge = AvatarRsvpBadge;

  @Input() public type: AvatarType;
  @Input() public shape: AvatarShape = 'oval';
  @Input() public size: AvatarSize = 'base';
  public context: InputSignal<AvatarContext> = input('candidate'); // set colors for initials type
  @Input() public fontSize: AvatarFontSize;

  @Input() public imageSrc: string | null;
  @Input() public isOnline: boolean = false;
  @Input() public avatarTpl?: TemplateRef<any>;
  @Input() public fullName?: string;
  @Input() public imageStyles: object;
  // isColorized property overrides backgroundColor
  @Input() public isColorized: boolean = false;
  @Input() public hasUnreadNotifications: boolean = false;
  @Input() public tooltipTplRef: TemplateRef<any>;
  @Input() public iconName: IconName;
  @Input() public initials?: string;
  @Input() public customIconSize?: IconSize;
  @Input() public customIconBackgroundColor?: AvatarToken;
  @Input() public rsvpBadge: AvatarRsvpBadge;
  public customIconColor: InputSignal<IconToken | undefined> = input(undefined);

  public imageError = output<Event>();

  public _avatarPlaceholder: ElementRef<HTMLDivElement>;
  @ViewChild('avatarPlaceholder')
  public set avatarPlaceholder(elementRef: ElementRef<HTMLDivElement>) {
    this._avatarPlaceholder = elementRef;
  }
  public get avatarPlaceholder() {
    return this._avatarPlaceholder;
  }

  @ViewChild('avatarIconWrapper')
  public avatarIconWrapper?: ElementRef<HTMLDivElement>;

  public nameInitials: string | null;
  public iconSize: IconSize;
  public iconColor: Signal<IconToken> = computed(() => {
    if (this.customIconColor()) {
      return this.customIconColor()!;
    }

    if (this.context() === 'candidate') {
      return 'colorIconDefault';
    }

    return 'colorIconOncomponentDefault';
  });

  public initials$: BehaviorSubject<string | null> = new BehaviorSubject(null);

  private themeClassSub: Subscription;
  private currentThemeClass: string;
  private ngOnDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private elRef: ElementRef,
    private renderer2: Renderer2,
  ) {}

  public ngAfterViewInit(): void {
    this.setAvatarColors();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.fullName || changes.initials) {
      this.nameInitials = this.resolveInitials();
      this.initials$.next(this.nameInitials);
    }

    if (changes.size && this.size) {
      this.iconSize = this.mapSizeToIconSize(this.size);
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();

    if (this.themeClassSub) {
      this.themeClassSub.unsubscribe();
    }
  }

  private setAvatarColors(): void {
    if (this.avatarIconWrapper?.nativeElement && this.customIconBackgroundColor) {
      this.renderer2.setStyle(
        this.avatarIconWrapper.nativeElement,
        'background-color',
        tokenToCssVar(this.customIconBackgroundColor),
      );
    }

    if (!this.isColorized || !this.nameInitials || !this.avatarPlaceholder?.nativeElement) return;

    this.initials$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(initials => {
      const code = initials!.charCodeAt(0);
      const backgroundTokensArray = avatarTokenSet.map(set => set.backgroundColor);
      const textTokensArray = avatarTokenSet.map(set => set.color);
      const index = code % avatarTokenSet.length;

      this.setStyleToken('background-color', backgroundTokensArray[index]);
      this.setStyleToken('color', textTokensArray[index]);
    });
  }

  private resolveInitials() {
    if (this.initials) {
      return this.initials;
    } else if (this.fullName) {
      const [firstName, ...lastName] = this.fullName.trim().split(' ');
      const char1 = firstName.trim() ? firstName[0].trim().toUpperCase() : '';
      const char2 = lastName.length ? lastName.join('').trim()[0].toUpperCase() : '';
      return char1 + char2;
    } else {
      return null;
    }
  }

  private toggleThemeClass(className: string): void {
    if (this.currentThemeClass) {
      this.renderer2.removeClass(this.elRef.nativeElement, this.currentThemeClass);
    }

    this.renderer2.addClass(this.elRef.nativeElement, className);
    this.currentThemeClass = className;
  }

  private setStyleToken(style: 'background-color' | 'color', token: string): void {
    this.renderer2.setStyle(this.avatarPlaceholder.nativeElement, style, tokenToCssVar(token));
  }

  private mapSizeToIconSize(size: AvatarSize) {
    switch (size) {
      case 'small-x':
        return 'small-xxx';
      case 'medium':
        return 'medium';
      case 'large':
        return 'large';
      case 'base':
      case 'small':
      default:
        return 'small-x';
    }
  }
}
